import React, { useState, useEffect, useMemo } from "react";
import {
  Box,
  Container,
  Heading,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Button,
  Select,
  Input,
  HStack,
  VStack,
  Spinner,
  Text,
  Badge,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalCloseButton,
  ModalFooter,
  useDisclosure,
  Stack,
  FormControl,
  FormLabel,
  TableContainer,
  Icon,
} from "@chakra-ui/react";
import { ArrowBackIcon } from "@chakra-ui/icons";
import { Link, useNavigate } from "react-router-dom";
import { useFindMany, useUser } from "@gadgetinc/react";
import { api } from "../api";
import { FiEye, FiEdit, FiChevronUp, FiChevronDown } from "react-icons/fi";

export default function MyEvaluations() {
  const user = useUser(api);
  const navigate = useNavigate();
  const { isOpen: isViewOpen, onOpen: onViewOpen, onClose: onViewClose } = useDisclosure();
  const { isOpen: isEditOpen, onOpen: onEditOpen, onClose: onEditClose } = useDisclosure();

  // Filter state
  const [selectedCohort, setSelectedCohort] = useState("");
  const [selectedStatus, setSelectedStatus] = useState("");
  const [selectedSource, setSelectedSource] = useState("");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [searchQuery, setSearchQuery] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");

  // Sort state
  const [sortField, setSortField] = useState("evaluationDate");
  const [sortDirection, setSortDirection] = useState("Descending");

  // Modal state
  const [selectedEvaluation, setSelectedEvaluation] = useState(null);

  // Debounce search
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(searchQuery);
    }, 300);

    return () => clearTimeout(timer);
  }, [searchQuery]);

  // Build filter object
  const filter = useMemo(() => {
    if (!user?.id) return null;

    const conditions = [
      // Always filter by current user's scenarios
      {
        scenario: {
          createdBy: {
            id: { equals: user.id },
          },
        },
      },
    ];

    // Cohort filter
    if (selectedCohort) {
      conditions.push({
        cohort: {
          id: { equals: selectedCohort },
        },
      });
    }

    // Status filter
    if (selectedStatus) {
      conditions.push({
        status: { equals: selectedStatus },
      });
    }

    // Source filter
    if (selectedSource === "direct") {
      conditions.push({
        shareToken: { isSet: false },
      });
    } else if (selectedSource === "shared") {
      conditions.push({
        shareToken: { isSet: true },
      });
    }

    // Date range filters
    if (startDate) {
      conditions.push({
        evaluationDate: { greaterThanOrEqual: new Date(startDate).toISOString() },
      });
    }
    if (endDate) {
      conditions.push({
        evaluationDate: { lessThanOrEqual: new Date(endDate).toISOString() },
      });
    }

    // Search filter (student name or email)
    if (debouncedSearch) {
      conditions.push({
        OR: [
          {
            student: {
              firstName: { matches: debouncedSearch },
            },
          },
          {
            student: {
              lastName: { matches: debouncedSearch },
            },
          },
          {
            student: {
              email: { matches: debouncedSearch },
            },
          },
        ],
      });
    }

    return { AND: conditions };
  }, [user?.id, selectedCohort, selectedStatus, selectedSource, startDate, endDate, debouncedSearch]);

  // Build sort object
  const sort = useMemo(() => {
    return { [sortField]: sortDirection };
  }, [sortField, sortDirection]);

  // Fetch evaluations
  const [{ data: evaluations, fetching: evaluationsFetching, error: evaluationsError }] = useFindMany(
    api.ph4456Evaluation,
    {
      filter,
      sort,
      select: {
        id: true,
        evaluationDate: true,
        overallScore: true,
        status: true,
        student: {
          id: true,
          firstName: true,
          lastName: true,
          email: true,
        },
        scenario: {
          id: true,
          title: true,
        },
        cohort: {
          id: true,
          name: true,
        },
        shareToken: {
          id: true,
        },
        comments: true,
        criticalFailures: true,
        completionTime: true,
      },
      first: 250,
    }
  );

  // Fetch cohorts for filter
  const [{ data: cohorts }] = useFindMany(api.cohort, {
    select: {
      id: true,
      name: true,
    },
    first: 250,
  });

  // Handle sort column click
  const handleSort = (field) => {
    if (sortField === field) {
      setSortDirection(sortDirection === "Ascending" ? "Descending" : "Ascending");
    } else {
      setSortField(field);
      setSortDirection("Ascending");
    }
  };

  // Format date
  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    return new Date(dateString).toLocaleDateString();
  };

  // Get status color
  const getStatusColor = (status) => {
    switch (status) {
      case "draft":
        return "yellow";
      case "completed":
        return "green";
      case "reviewed":
        return "blue";
      default:
        return "gray";
    }
  };

  // Handle view
  const handleView = (evaluation) => {
    setSelectedEvaluation(evaluation);
    onViewOpen();
  };

  // Handle edit
  const handleEdit = (evaluation) => {
    setSelectedEvaluation(evaluation);
    onEditOpen();
  };

  // Render sort icon
  const renderSortIcon = (field) => {
    if (sortField !== field) return null;
    return sortDirection === "Ascending" ? <Icon as={FiChevronUp} /> : <Icon as={FiChevronDown} />;
  };

  if (!user) {
    return (
      <Container maxW="container.xl" py={8}>
        <Spinner />
      </Container>
    );
  }

  return (
    <Container maxW="container.xl" py={8}>
      <VStack spacing={6} align="stretch">
        <HStack spacing={4} align="center">
          <Button
            as={Link}
            to="/my-scenarios"
            leftIcon={<ArrowBackIcon />}
            variant="ghost"
            colorScheme="blue"
          >
            Back to My Scenarios
          </Button>
          <Heading size="lg">My Scenario Evaluations</Heading>
        </HStack>

        {/* Filters */}
        <Box bg="gray.800" p={4} borderRadius="md" shadow="sm">
          <VStack spacing={4} align="stretch">
            <HStack spacing={4} wrap="wrap">
              <FormControl maxW="200px">
                <FormLabel fontSize="sm">Cohort</FormLabel>
                <Select
                  placeholder="All Cohorts"
                  value={selectedCohort}
                  onChange={(e) => setSelectedCohort(e.target.value)}
                  size="sm"
                >
                  {cohorts?.map((cohort) => (
                    <option key={cohort.id} value={cohort.id}>
                      {cohort.name}
                    </option>
                  ))}
                </Select>
              </FormControl>

              <FormControl maxW="200px">
                <FormLabel fontSize="sm">Status</FormLabel>
                <Select
                  placeholder="All Statuses"
                  value={selectedStatus}
                  onChange={(e) => setSelectedStatus(e.target.value)}
                  size="sm"
                >
                  <option value="draft">Draft</option>
                  <option value="completed">Completed</option>
                  <option value="reviewed">Reviewed</option>
                </Select>
              </FormControl>

              <FormControl maxW="200px">
                <FormLabel fontSize="sm">Source</FormLabel>
                <Select
                  placeholder="All Sources"
                  value={selectedSource}
                  onChange={(e) => setSelectedSource(e.target.value)}
                  size="sm"
                >
                  <option value="direct">Direct</option>
                  <option value="shared">Shared Link</option>
                </Select>
              </FormControl>

              <FormControl maxW="200px">
                <FormLabel fontSize="sm">Start Date</FormLabel>
                <Input type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)} size="sm" />
              </FormControl>

              <FormControl maxW="200px">
                <FormLabel fontSize="sm">End Date</FormLabel>
                <Input type="date" value={endDate} onChange={(e) => setEndDate(e.target.value)} size="sm" />
              </FormControl>
            </HStack>

            <FormControl maxW="400px">
              <FormLabel fontSize="sm">Search Student</FormLabel>
              <Input
                placeholder="Search by student name or email..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                size="sm"
              />
            </FormControl>
          </VStack>
        </Box>

        {/* Table */}
        <Box bg="gray.800" borderRadius="md" shadow="sm" overflowX="auto">
          {evaluationsFetching ? (
            <Box p={8} textAlign="center">
              <Spinner size="lg" />
              <Text mt={4} color="white">Loading evaluations...</Text>
            </Box>
          ) : evaluationsError ? (
            <Box p={8} textAlign="center">
              <Text color="red.500">Error loading evaluations: {evaluationsError.message}</Text>
            </Box>
          ) : !evaluations || evaluations.length === 0 ? (
            <Box p={8} textAlign="center">
              <Text color="gray.400" fontSize="lg">
                No evaluations found. Try adjusting your filters.
              </Text>
            </Box>
          ) : (
            <TableContainer>
              <Table variant="simple" size="sm">
                <Thead bg="gray.700">
                  <Tr>
                    <Th cursor="pointer" onClick={() => handleSort("student")}>
                      <HStack spacing={1}>
                        <Text>Student Name</Text>
                        {renderSortIcon("student")}
                      </HStack>
                    </Th>
                    <Th>Student Email</Th>
                    <Th cursor="pointer" onClick={() => handleSort("scenario")}>
                      <HStack spacing={1}>
                        <Text>Scenario</Text>
                        {renderSortIcon("scenario")}
                      </HStack>
                    </Th>
                    <Th>Cohort</Th>
                    <Th cursor="pointer" onClick={() => handleSort("evaluationDate")}>
                      <HStack spacing={1}>
                        <Text>Evaluation Date</Text>
                        {renderSortIcon("evaluationDate")}
                      </HStack>
                    </Th>
                    <Th cursor="pointer" onClick={() => handleSort("overallScore")}>
                      <HStack spacing={1}>
                        <Text>Score</Text>
                        {renderSortIcon("overallScore")}
                      </HStack>
                    </Th>
                    <Th cursor="pointer" onClick={() => handleSort("status")}>
                      <HStack spacing={1}>
                        <Text>Status</Text>
                        {renderSortIcon("status")}
                      </HStack>
                    </Th>
                    <Th>Source</Th>
                    <Th>Actions</Th>
                  </Tr>
                </Thead>
                <Tbody>
                  {evaluations.map((evaluation, index) => (
                    <Tr
                      key={evaluation.id}
                      bg={index % 2 === 0 ? "gray.800" : "gray.700"}
                      _hover={{ bg: "gray.600" }}
                      transition="background 0.2s"
                    >
                      <Td>
                        {evaluation.student?.firstName} {evaluation.student?.lastName}
                      </Td>
                      <Td>{evaluation.student?.email || "N/A"}</Td>
                      <Td>{evaluation.scenario?.title || "N/A"}</Td>
                      <Td>{evaluation.cohort?.name || "N/A"}</Td>
                      <Td>{formatDate(evaluation.evaluationDate)}</Td>
                      <Td fontWeight="bold">{evaluation.overallScore || "N/A"}</Td>
                      <Td>
                        <Badge colorScheme={getStatusColor(evaluation.status)}>{evaluation.status}</Badge>
                      </Td>
                      <Td>
                        <Badge colorScheme={evaluation.shareToken?.id ? "purple" : "green"}>
                          {evaluation.shareToken?.id ? "Shared Link" : "Direct"}
                        </Badge>
                      </Td>
                      <Td>
                        <Button
                          size="xs"
                          colorScheme="blue"
                          leftIcon={<FiEye />}
                          onClick={() => navigate(`/evaluation/${evaluation.id}?type=ph4456`)}
                        >
                          View Evaluation
                        </Button>
                      </Td>
                    </Tr>
                  ))}
                </Tbody>
              </Table>
            </TableContainer>
          )}
        </Box>
      </VStack>

      {/* View Modal */}
      <Modal isOpen={isViewOpen} onClose={onViewClose} size="xl">
        <ModalOverlay />
        <ModalContent bg="gray.800" color="white">
          <ModalHeader>Evaluation Details</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            {selectedEvaluation && (
              <VStack spacing={4} align="stretch">
                <Box>
                  <Text fontWeight="bold">Student:</Text>
                  <Text>
                    {selectedEvaluation.student?.firstName} {selectedEvaluation.student?.lastName} (
                    {selectedEvaluation.student?.email})
                  </Text>
                </Box>
                <Box>
                  <Text fontWeight="bold">Scenario:</Text>
                  <Text>{selectedEvaluation.scenario?.title}</Text>
                </Box>
                <Box>
                  <Text fontWeight="bold">Cohort:</Text>
                  <Text>{selectedEvaluation.cohort?.name || "N/A"}</Text>
                </Box>
                <Box>
                  <Text fontWeight="bold">Evaluation Date:</Text>
                  <Text>{formatDate(selectedEvaluation.evaluationDate)}</Text>
                </Box>
                <Box>
                  <Text fontWeight="bold">Overall Score:</Text>
                  <Text fontSize="2xl" fontWeight="bold" color="blue.600">
                    {selectedEvaluation.overallScore || "N/A"}
                  </Text>
                </Box>
                <Box>
                  <Text fontWeight="bold">Status:</Text>
                  <Badge colorScheme={getStatusColor(selectedEvaluation.status)} fontSize="md">
                    {selectedEvaluation.status}
                  </Badge>
                </Box>
                <Box>
                  <Text fontWeight="bold">Source:</Text>
                  <Badge colorScheme={selectedEvaluation.shareToken?.id ? "purple" : "green"} fontSize="md">
                    {selectedEvaluation.shareToken?.id ? "Shared Link" : "Direct"}
                  </Badge>
                </Box>
                {selectedEvaluation.completionTime && (
                  <Box>
                    <Text fontWeight="bold">Completion Time:</Text>
                    <Text>{selectedEvaluation.completionTime} minutes</Text>
                  </Box>
                )}
                {selectedEvaluation.comments && (
                  <Box>
                    <Text fontWeight="bold">Comments:</Text>
                    <Text>{selectedEvaluation.comments}</Text>
                  </Box>
                )}
                {selectedEvaluation.criticalFailures && (
                  <Box>
                    <Text fontWeight="bold">Critical Failures:</Text>
                    <Text>{JSON.stringify(selectedEvaluation.criticalFailures, null, 2)}</Text>
                  </Box>
                )}
              </VStack>
            )}
          </ModalBody>
          <ModalFooter>
            <Button colorScheme="blue" onClick={onViewClose}>
              Close
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      {/* Edit Modal */}
      <Modal isOpen={isEditOpen} onClose={onEditClose} size="xl">
        <ModalOverlay />
        <ModalContent bg="gray.800" color="white">
          <ModalHeader>Edit Evaluation</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            {selectedEvaluation && (
              <VStack spacing={4} align="stretch">
                <Text color="orange.500" fontWeight="bold">
                  This evaluation was submitted via a shared link. You can override or revise the evaluation here.
                </Text>
                <Box>
                  <Text fontWeight="bold">Student:</Text>
                  <Text>
                    {selectedEvaluation.student?.firstName} {selectedEvaluation.student?.lastName}
                  </Text>
                </Box>
                <Box>
                  <Text fontWeight="bold">Current Score:</Text>
                  <Text fontSize="xl">{selectedEvaluation.overallScore || "N/A"}</Text>
                </Box>
                {/* Add form fields for editing here */}
                <Text fontSize="sm" color="gray.400">
                  Edit functionality to be implemented...
                </Text>
              </VStack>
            )}
          </ModalBody>
          <ModalFooter>
            <Button variant="ghost" mr={3} onClick={onEditClose}>
              Cancel
            </Button>
            <Button colorScheme="orange">Save Changes</Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </Container>
  );
}